import React, { memo, useCallback, useEffect, useState } from 'react'

import { DragDropProvider } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import type { InfiniteData } from '@tanstack/react-query'
import type { IconType } from 'react-icons'
import { GoGrabber } from 'react-icons/go'
import {
  HiMiniPlus,
  HiOutlineQuestionMarkCircle,
  HiOutlineTrash,
  HiOutlineUserGroup,
} from 'react-icons/hi2'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'
import { v4 } from 'uuid'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { type DragEvent } from '@/components/dnd/v2/KanbanBoard'
import TextArea from '@/components/form/TextArea'
import TextInput from '@/components/form/TextInput'
import { ColorIndicator } from '@/components/tags/ColorIndicator'
import { ColorSelector } from '@/components/tags/ColorSelector'
import {
  EditTagsDialogContextProvider,
  useEditTagDialogTagIsSelected,
  useEditTagsDialogAddNewTag,
  useEditTagsDialogClearDirtyState,
  useEditTagsDialogDeletedTagIds,
  useEditTagsDialogEditableTagIds,
  useEditTagsDialogEditableTags,
  useEditTagsDialogGetTagChangeStatus,
  useEditTagsDialogHasChanges,
  useEditTagsDialogHasValidationErrors,
  useEditTagsDialogInit,
  useEditTagsDialogMarkTagForDeletion,
  useEditTagsDialogReset,
  useEditTagsDialogMoveTag,
  useEditTagsDialogSelectTag,
  useEditTagsDialogSelectedTagId,
  useEditTagsDialogTag,
  useEditTagsDialogTagChangeStatus,
  useEditTagsDialogTagType,
  useEditTagsDialogTagValidationError,
  useEditTagsDialogUpdateTagDetails,
} from '@/components/tags/EditTagsDialog/context'
import type { EditableTag } from '@/components/tags/EditTagsDialog/types'

import { compareTagsByPosition } from '@/helpers/tags'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useTags } from '@/hooks/tags/useTags'
import { useUpsertTags } from '@/hooks/tags/useUpsertTags'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Tag, TagType } from '@/types/Tag'

const MAX_TAG_DESCRIPTION_LENGTH = 512
const UNTITLED_TAG_NAME = 'Untitled'
const HEADER_HEIGHT = 28

// Selector to transform Tags to EditableTags with sorting
const selectEditableTags = (data: InfiniteData<PaginatedResponse<Tag>>) => {
  return data.pages
    .flatMap((page) => page.data)
    .map((tag) => ({ ...tag, isNew: false }) as EditableTag)
    .sort(compareTagsByPosition)
}

const isFolderTagType = (tagType: TagType) =>
  tagType === 'organization_folder' || tagType === 'pursuit_folder'

const getDialogTitle = (tagType: TagType) =>
  isFolderTagType(tagType) ? 'Manage Folders' : 'Manage Tags'

const getDialogDescription = (tagType: TagType) =>
  isFolderTagType(tagType)
    ? 'Organize your library with folders. Set a description and color so folders are easy to distinguish.'
    : 'Organize your content with tags. Set a description so that Sweetspot AI knows how to use the tagged content in workflows.'

const getTagNoun = (tagType: TagType) =>
  isFolderTagType(tagType) ? 'Folder' : 'Tag'

const createNewTag = (
  userId: UUID,
  orgId: UUID,
  tagType: TagType = 'organization',
  parentId: UUID | null = null,
  pursuitId: UUID | null = null,
): EditableTag => {
  return {
    id: v4() as UUID,
    name: '',
    color: 'zinc',
    description: '',
    type: tagType,
    isNew: true,
    user_id: userId,
    created_at: new Date().toISOString(),
    last_modified: new Date().toISOString(),
    organization_id: orgId,
    parent_id: parentId,
    pursuit_id: pursuitId,
    position: null,
  }
}

const useAddNewTag = (parentId: UUID | null, pursuitId: UUID | null) => {
  const { user } = useAuthInfoWithThrow()
  const { activeOrgId } = useActiveOrgId()
  const tagType = useEditTagsDialogTagType()

  const addNewTagToStore = useEditTagsDialogAddNewTag()
  const selectTag = useEditTagsDialogSelectTag()

  return useCallback(() => {
    const newTag = createNewTag(
      user.userId as UUID,
      activeOrgId as UUID,
      tagType,
      parentId,
      pursuitId,
    )
    addNewTagToStore(newTag)
    selectTag(newTag.id)
  }, [
    user.userId,
    activeOrgId,
    tagType,
    parentId,
    pursuitId,
    addNewTagToStore,
    selectTag,
  ])
}

interface LabelProps {
  variant?: 'new' | 'modified' | 'unsaved'
}

const Label: React.FC<LabelProps> = memo(({ variant = 'modified' }) => {
  switch (variant) {
    case 'new':
      return (
        <span className="inline-flex items-center rounded-full bg-accent-50 dark:bg-accent-700/50 px-2 text-[13px] font-medium text-accent-700 dark:text-accent-100 border border-accent-200 dark:border-accent-700">
          New
        </span>
      )
    case 'modified':
      return (
        <span className="inline-flex items-center rounded-full bg-orange-50 dark:bg-orange-700/50 px-2 text-[13px] font-medium text-orange-700 dark:text-orange-100 border border-orange-200 dark:border-orange-700">
          Modified
        </span>
      )
    case 'unsaved':
      return (
        <span className="inline-flex items-center rounded-full bg-red-50 dark:bg-red-700/50 px-2 text-[13px] font-medium text-red-700 dark:text-red-100 border border-red-200 dark:border-red-700">
          Unsaved
        </span>
      )
    default:
      const exhaustiveCheck: never = variant
      return exhaustiveCheck
  }
})

Label.displayName = 'EditTagsDialog.Label'

interface TagSidebarItemContentProps {
  tag: EditableTag
}

const TagSidebarItemContent: React.FC<TagSidebarItemContentProps> = memo(
  ({ tag }) => {
    const changeStatus = useEditTagsDialogTagChangeStatus(tag.id)
    const isSelected = useEditTagDialogTagIsSelected(tag.id)
    const selectTag = useEditTagsDialogSelectTag()

    const handleClick = useCallback(() => {
      selectTag(tag.id)
    }, [tag.id, selectTag])

    return (
      <button
        type="button"
        onClick={handleClick}
        className={twMerge(
          'w-full max-w-full truncate flex items-center gap-2 px-3 h-10 rounded-lg text-left transition-all duration-150',
          isSelected
            ? 'bg-primary-200 dark:bg-primary-700/70 text-primary-900 dark:text-primary-100'
            : 'text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800/50',
        )}
      >
        <div className="flex items-center gap-2 flex-shrink-0">
          <ColorIndicator color={tag.color} className="w-3 h-3" />
        </div>
        <div className="flex-1 min-w-0">
          {tag.name ? (
            <div className="font-medium truncate text-sm text-primary-900 dark:text-primary-100">
              {tag.name}
            </div>
          ) : (
            <div className="font-medium truncate text-sm text-primary-500 dark:text-primary-400">
              {UNTITLED_TAG_NAME}
            </div>
          )}
        </div>
        {changeStatus.isNew && <Label variant="new" />}
        {!changeStatus.isNew && changeStatus.hasChanges && (
          <Label variant="modified" />
        )}
      </button>
    )
  },
)

TagSidebarItemContent.displayName = 'EditTagsDialog.TagSidebarItemContent'

interface TagSidebarItemProps {
  tagId: UUID
  index: number
  sortable: boolean
}

const TagSidebarItem: React.FC<TagSidebarItemProps> = memo(
  ({ tagId, index, sortable }) => {
    const tag = useEditTagsDialogTag(tagId)
    const { ref, isDragSource } = useSortable({
      id: tagId,
      index,
      type: 'item',
      group: 'tags',
      transition: {
        duration: 150,
        idle: false,
      },
      disabled: !sortable,
    })
    if (!tag) {
      return null
    }
    return (
      <div
        ref={ref}
        className={twMerge(
          'flex items-center gap-1',
          sortable && 'cursor-grab',
          isDragSource && 'opacity-60',
        )}
      >
        {sortable && (
          <GoGrabber
            size={16}
            className="flex-shrink-0 text-primary-400 dark:text-primary-500"
          />
        )}
        <div className="flex-1 min-w-0">
          <TagSidebarItemContent tag={tag} />
        </div>
      </div>
    )
  },
)

TagSidebarItem.displayName = 'EditTagsDialog.TagSidebarItem'

interface TagPageProps {
  tagId: UUID
}

const TagPage: React.FC<TagPageProps> = memo(({ tagId }) => {
  const tag = useEditTagsDialogTag(tagId)

  if (!tag) {
    return null
  }

  return <TagPageForm tag={tag} />
})

TagPage.displayName = 'EditTagsDialog.TagPage'

interface TagPageFormProps {
  tag: EditableTag
}

const TagPageForm: React.FC<TagPageFormProps> = memo(({ tag }) => {
  const updateTagDetails = useEditTagsDialogUpdateTagDetails()
  const markTagForDeletion = useEditTagsDialogMarkTagForDeletion()

  const handleInputChange = useCallback(
    (field: keyof EditableTag, value: string) => {
      updateTagDetails(tag.id, { [field]: value })
    },
    [updateTagDetails, tag.id],
  )

  const nameError = useEditTagsDialogTagValidationError(tag.id)

  const handleDelete = useCallback(() => {
    markTagForDeletion(tag.id)
  }, [markTagForDeletion, tag.id])

  const handleNameInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleInputChange('name', e.target.value)
    },
    [handleInputChange],
  )

  const handleColorChange = useCallback(
    (color: string) => {
      handleInputChange('color', color)
    },
    [handleInputChange],
  )

  const handleDescriptionInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      handleInputChange('description', e.target.value)
    },
    [handleInputChange],
  )

  return (
    <div className="flex-1 flex flex-col h-full w-full divide-y divide-primary-200 dark:divide-primary-700">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 w-full truncate">
        <ColorIndicator color={tag.color} className="w-4 h-4" />
        <div className="flex-1 min-w-0 w-full truncate">
          <div
            style={{ height: HEADER_HEIGHT }}
            className="flex items-center justify-between gap-2 min-w-0 w-full truncate"
          >
            {tag.name ? (
              <span className="text-lg font-medium text-primary-900 dark:text-primary-100 truncate">
                {tag.name}
              </span>
            ) : (
              <span className="text-lg font-medium text-primary-500 dark:text-primary-400 truncate">
                {UNTITLED_TAG_NAME}
              </span>
            )}
            {tag.isNew && (
              <div className="flex items-center gap-2">
                <Label variant="unsaved" />
                <Button
                  type="button"
                  variant="outline-compact"
                  onClick={handleDelete}
                  className="flex-shrink-0"
                >
                  <span>Cancel</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-primary-900">
        <div className="px-5 py-4 space-y-5">
          {/* Basic Details Section */}
          <div className="space-y-4">
            <div className="flex gap-4 w-full">
              <div className="flex-grow space-y-2">
                <label className="block text-sm font-medium text-primary-900 dark:text-primary-100">
                  Name
                </label>
                <TextInput
                  value={tag.name}
                  onChange={handleNameInputChange}
                  placeholder="Tag name"
                  className={twMerge(
                    'w-full resize-none',
                    nameError
                      ? 'border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500'
                      : '',
                  )}
                />
                <div className="flex justify-between items-center text-xs">
                  <span className="text-primary-500 dark:text-primary-400">
                    {tag.name?.length || 0} characters
                  </span>
                  {nameError ? (
                    <span className="text-red-600 dark:text-red-400">
                      {nameError}
                    </span>
                  ) : (
                    <span></span>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0 space-y-2">
                <label className="block text-sm font-medium text-primary-900 dark:text-primary-100">
                  Color
                </label>
                <ColorSelector
                  color={tag.color}
                  onChange={handleColorChange}
                  triggerProps={{
                    className:
                      'w-9 h-9 hover:scale-105 transition-transform cursor-pointer',
                    children: (
                      <ColorIndicator color={tag.color} className="w-5 h-5" />
                    ),
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label className="block text-sm font-medium text-primary-900 dark:text-primary-100">
                  Description
                </label>
                <Tooltip>
                  <TooltipTrigger>
                    <HiOutlineQuestionMarkCircle
                      size={16}
                      className="text-primary-500 dark:text-primary-400"
                    />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[300px]">
                    <p>
                      This description will be used to guide Sweetspot AI when
                      any content tagged with this tag is used in an AI
                      workflow.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextArea
                value={tag.description || ''}
                onChange={handleDescriptionInputChange}
                placeholder="Enter tag description (optional)"
                className="w-full resize-none"
                rows={6}
                maxLength={MAX_TAG_DESCRIPTION_LENGTH}
              />
              <div className="text-xs text-primary-500 dark:text-primary-400 text-left">
                {tag.description?.length || 0} / {MAX_TAG_DESCRIPTION_LENGTH}{' '}
                characters
              </div>
            </div>
          </div>
          {!tag.isNew && (
            <>
              {/* Danger Zone */}
              <div className="space-y-4">
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-md p-3">
                  <h4 className="text-sm font-medium text-red-900 dark:text-red-100 mb-2">
                    Danger Zone
                  </h4>
                  <p className="text-xs text-red-700 dark:text-red-300 mb-3">
                    Once you delete a tag, the tag will be removed from all
                    content. This action cannot be undone.
                  </p>
                  <Button
                    type="button"
                    variant="outline-compact"
                    onClick={handleDelete}
                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border-red-200 dark:border-red-800 hover:border-red-300 dark:hover:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <HiOutlineTrash className="w-4 h-4" />
                    <span>Delete Tag</span>
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
})

TagPageForm.displayName = 'EditTagsDialog.TagPageForm'

const ValidationErrors: React.FC = memo(() => {
  const hasValidationErrors = useEditTagsDialogHasValidationErrors()
  const hasChanges = useEditTagsDialogHasChanges()

  return (
    <div className="text-sm">
      {hasValidationErrors ? (
        <div className="inline-flex items-center gap-2">
          <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
          <span className="text-red-700 dark:text-red-300">
            Please fix duplicate tag names
          </span>
        </div>
      ) : hasChanges ? (
        <div className="inline-flex items-center gap-2">
          <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
          <span className="text-orange-700 dark:text-orange-300">
            You have unsaved changes
          </span>
        </div>
      ) : null}
    </div>
  )
})

ValidationErrors.displayName = 'EditTagsDialog.ValidationErrors'

interface SaveButtonProps {
  onSuccess?: () => void
}

const SaveButton: React.FC<SaveButtonProps> = memo(({ onSuccess }) => {
  const { activeOrgId } = useActiveOrgId()
  const { mutateAsync: upsertTags } = useUpsertTags({ orgId: activeOrgId })
  const tagType = useEditTagsDialogTagType()

  const editableTags = useEditTagsDialogEditableTags()
  const deletedTagIds = useEditTagsDialogDeletedTagIds()
  const getTagChangeStatus = useEditTagsDialogGetTagChangeStatus()
  const hasValidationErrors = useEditTagsDialogHasValidationErrors()
  const hasChanges = useEditTagsDialogHasChanges()

  const clearDirtyState = useEditTagsDialogClearDirtyState()

  const [isSaving, setIsSaving] = useState(false)

  const handleSaveAll = useCallback(async () => {
    // Double-check for validation errors before saving
    if (hasValidationErrors) {
      toast.error('Please fix duplicate tag names')
      return
    }

    setIsSaving(true)
    try {
      // Filter out tags with empty names
      const validTags = editableTags.filter((tag) => tag.name.trim() !== '')

      // Sidebar order is the manual sort order; folders are not orderable.
      const isFolder = isFolderTagType(tagType)
      const positionById = new Map<UUID, number>()
      if (!isFolder) {
        validTags.forEach((tag, index) => positionById.set(tag.id, index))
      }

      const tagsToCreate = validTags
        .filter((tag) => tag.isNew)
        .map((tag) => ({
          name: tag.name,
          description: tag.description,
          color: tag.color,
          type: tag.type,
          parent_id: tag.parent_id,
          pursuit_id: tag.pursuit_id,
          position: positionById.get(tag.id),
        }))
      const tagsToUpdate = validTags
        .filter(
          (tag) =>
            !tag.isNew &&
            (getTagChangeStatus(tag.id).hasChanges ||
              (!isFolder && positionById.get(tag.id) !== tag.position)),
        )
        .map((tag) => ({
          id: tag.id,
          name: tag.name,
          description: tag.description,
          color: tag.color,
          parent_id: tag.parent_id,
          pursuit_id: tag.pursuit_id,
          position: positionById.get(tag.id),
        }))
      const tagsToDelete = deletedTagIds
      await upsertTags({ tagsToCreate, tagsToUpdate, tagsToDelete })
      toast.success(
        `${isFolderTagType(tagType) ? 'Folders' : 'Tags'} saved successfully.`,
      )
      clearDirtyState()
      onSuccess?.()
    } finally {
      setIsSaving(false)
    }
  }, [
    editableTags,
    hasValidationErrors,
    deletedTagIds,
    getTagChangeStatus,
    upsertTags,
    clearDirtyState,
    onSuccess,
    tagType,
  ])

  return (
    <Button
      type="button"
      variant="primary-compact"
      onClick={handleSaveAll}
      disabled={!hasChanges || hasValidationErrors || isSaving}
      isLoading={isSaving}
    >
      Save Changes
    </Button>
  )
})

SaveButton.displayName = 'EditTagsDialog.SaveButton'

interface TagListHeaderProps {
  onAddNewTag: () => void
}

const TagListHeader: React.FC<TagListHeaderProps> = memo(({ onAddNewTag }) => {
  const editableTagIds = useEditTagsDialogEditableTagIds()
  const tagType = useEditTagsDialogTagType()
  const tagNoun = getTagNoun(tagType)

  return (
    <div className="p-4 bg-white dark:bg-primary-900">
      <div
        style={{ height: HEADER_HEIGHT }}
        className="flex items-center justify-between"
      >
        <div>
          <h3 className="font-semibold text-primary-900 dark:text-primary-100">
            {tagNoun}s ({editableTagIds.length})
          </h3>
        </div>
        <Button
          type="button"
          variant="outline-compact"
          onClick={onAddNewTag}
          className="text-xs flex-shrink-0"
        >
          <HiMiniPlus size={16} />
          <span className="hidden md:inline">Add {tagNoun}</span>
        </Button>
      </div>
    </div>
  )
})

TagListHeader.displayName = 'EditTagsDialog.TagListHeader'

interface TagListProps {
  icon?: IconType
  onAddNewTag: () => void
}

const TagList: React.FC<TagListProps> = memo(
  ({ icon = HiOutlineUserGroup, onAddNewTag }) => {
    const Icon = icon

    const editableTagIds = useEditTagsDialogEditableTagIds()
    const moveTag = useEditTagsDialogMoveTag()
    const tagType = useEditTagsDialogTagType()
    const tagNoun = getTagNoun(tagType)
    const sortable = !isFolderTagType(tagType)

    // The move is computed inside the store from its current state via
    // dnd-kit's move(): dragOver fires once per row crossing, and a
    // render-captured array here would go stale between events and desync
    // the saved order from the visual order. dragEnd commits the final
    // projected index so the settled visual and the store always agree.
    const handleDragOver = useCallback(
      (event: DragEvent) => {
        const { source, target } = event.operation
        if (!source || !target || source.id === target.id) return
        moveTag(source, target)
      },
      [moveTag],
    )

    // dragEnd often reports target === source (the row under the pointer is
    // the dragged row after optimistic reordering); move() then reconciles
    // via the projected source index, so no equality guard here.
    const handleDragEnd = useCallback(
      (event: DragEvent) => {
        const { source, target } = event.operation
        if (!source || !target) return
        moveTag(source, target)
      },
      [moveTag],
    )

    return (
      // @ts-ignore -- dnd-kit provider prop typing (same as EditStages)
      <DragDropProvider onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {editableTagIds.length > 0 ? (
            editableTagIds.map((tagId, index) => (
              <TagSidebarItem
                key={tagId}
                tagId={tagId}
                index={index}
                sortable={sortable}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <Icon className="w-16 h-16 text-primary-300 dark:text-primary-600 mb-4" />
              <p className="text-sm text-primary-600 dark:text-primary-400 mb-4 font-medium">
                No {tagNoun.toLowerCase()}s yet
              </p>
              <Button
                type="button"
                variant="outline-compact"
                onClick={onAddNewTag}
              >
                <HiMiniPlus size={16} className="flex-shrink-0" />
                <span>Create your first {tagNoun.toLowerCase()}</span>
              </Button>
            </div>
          )}
        </div>
      </DragDropProvider>
    )
  },
)

TagList.displayName = 'EditTagsDialog.TagList'

interface EditTagsDialogContentProps {
  open: boolean
  setOpen: (open: boolean) => void
  icon?: IconType
  onAddNewTag: () => void
}

export const EditTagsDialogContent: React.FC<EditTagsDialogContentProps> = memo(
  ({ open, setOpen, icon = HiOutlineUserGroup, onAddNewTag }) => {
    const Icon = icon

    const selectedTagId = useEditTagsDialogSelectedTagId()
    const tagType = useEditTagsDialogTagType()

    const reset = useEditTagsDialogReset()

    const handleClose = useCallback(() => {
      setOpen(false)
    }, [setOpen])

    // Reset store when dialog closes
    useEffect(() => {
      if (!open) {
        reset()
      }
    }, [open, reset])

    return (
      <div className="flex flex-col h-full w-full divide-y divide-primary-200 dark:divide-primary-700">
        {/* Header */}
        <div className="px-5 py-4 bg-white dark:bg-primary-900">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {getDialogTitle(tagType)}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {getDialogDescription(tagType)}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden divide-x divide-primary-200 dark:divide-primary-700">
          {/* Sidebar */}
          <div className="w-64 min-w-64 max-w-64 2xl:w-80 2xl:min-w-80 2xl:max-w-80 divide-y divide-primary-200 dark:divide-primary-700 bg-primary-50 dark:bg-default-dark flex flex-col">
            <TagListHeader onAddNewTag={onAddNewTag} />
            <TagList icon={icon} onAddNewTag={onAddNewTag} />
          </div>

          {/* max-w-[calc(75vw-324px)] is the width of the dialog content as defined below minus the width of the sidebar */}
          <div className="flex flex-col flex-1">
            {selectedTagId ? (
              <TagPage key={selectedTagId} tagId={selectedTagId} />
            ) : (
              <div className="flex-1 flex items-center justify-center text-primary-500 dark:text-primary-400">
                <div className="text-center">
                  <Icon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-xl font-medium mb-2 text-primary-700 dark:text-primary-300">
                    Select a {getTagNoun(tagType).toLowerCase()} to edit
                  </p>
                  <p className="text-sm max-w-sm">
                    Choose a {getTagNoun(tagType).toLowerCase()} from the
                    sidebar to view and edit its details
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20">
          <div className="flex items-center justify-between">
            <ValidationErrors />
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline-compact"
                onClick={handleClose}
              >
                Close
              </Button>
              <SaveButton onSuccess={handleClose} />
            </div>
          </div>
        </div>
      </div>
    )
  },
)

EditTagsDialogContent.displayName = 'EditTagsDialog.EditTagsDialogContent'

interface SelectedTagIdListenerProps {
  tagId: UUID
}

const SelectedTagIdListener: React.FC<SelectedTagIdListenerProps> = ({
  tagId,
}) => {
  const selectTag = useEditTagsDialogSelectTag()
  useEffect(() => {
    selectTag(tagId)
  }, [tagId, selectTag])
  return null
}

interface CreateNewTagListenerProps {
  open: boolean
  createNewTagOnOpen: boolean
  onAddNewTag: () => void
}

const CreateNewTagListener: React.FC<CreateNewTagListenerProps> = ({
  open,
  createNewTagOnOpen,
  onAddNewTag,
}) => {
  useEffect(() => {
    if (open && createNewTagOnOpen) {
      const timeoutId = setTimeout(() => {
        // run this after the dialog has been opened;
        // not sure why but running it in the same tick as the dialog opening
        // causes it to get lost in the state update
        onAddNewTag()
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [open, createNewTagOnOpen, onAddNewTag])

  return null
}

interface InitTagsListenerProps {
  open: boolean
  tags: EditableTag[]
  addTagOnInit?: boolean
  onAddNewTag: () => void
}

const InitTagsListener: React.FC<InitTagsListenerProps> = ({
  open,
  tags,
  addTagOnInit,
  onAddNewTag,
}) => {
  const init = useEditTagsDialogInit()
  useEffect(() => {
    if (open && tags) {
      init(tags)
      if (addTagOnInit) {
        onAddNewTag()
      }
    }
  }, [open, addTagOnInit, init, onAddNewTag, tags])
  return null
}

interface EditTagsDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  addTagOnInit?: boolean
  createNewTagOnOpen?: boolean
  initialSelectedTagId?: UUID
  tagType?: TagType
  icon?: IconType
  newTagParentId?: UUID | null
  newTagPursuitId?: UUID | null
}

interface EditTagsDialogProviderContentProps extends EditTagsDialogProps {
  tags: EditableTag[]
}

const EditTagsDialogProviderContent: React.FC<
  EditTagsDialogProviderContentProps
> = ({
  open,
  setOpen,
  initialSelectedTagId,
  addTagOnInit = false,
  createNewTagOnOpen = false,
  tagType,
  icon = HiOutlineUserGroup,
  newTagParentId = null,
  newTagPursuitId = null,
  tags,
}) => {
  const handleAddNewTag = useAddNewTag(newTagParentId, newTagPursuitId)

  return (
    <>
      <CreateNewTagListener
        open={open}
        createNewTagOnOpen={createNewTagOnOpen}
        onAddNewTag={handleAddNewTag}
      />
      {initialSelectedTagId && (
        <SelectedTagIdListener tagId={initialSelectedTagId} />
      )}
      <InitTagsListener
        open={open}
        tags={tags}
        addTagOnInit={addTagOnInit}
        onAddNewTag={handleAddNewTag}
      />
      <EditTagsDialogContent
        open={open}
        setOpen={setOpen}
        icon={icon}
        onAddNewTag={handleAddNewTag}
      />
    </>
  )
}

export const EditTagsDialogRoot: React.FC<EditTagsDialogProps> = ({
  open,
  setOpen,
  initialSelectedTagId,
  addTagOnInit = false,
  createNewTagOnOpen = false,
  tagType,
  icon = HiOutlineUserGroup,
  newTagParentId = null,
  newTagPursuitId = null,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: tags } = useTags({
    orgId: activeOrgId,
    tagType,
    selector: selectEditableTags,
  })

  if (!tags) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full">
        <Spinner />
      </div>
    )
  }

  return (
    <EditTagsDialogContextProvider
      initStoreArgs={{
        tags,
        initialSelectedTagId,
        tagType,
      }}
    >
      <EditTagsDialogProviderContent
        open={open}
        setOpen={setOpen}
        addTagOnInit={addTagOnInit}
        createNewTagOnOpen={createNewTagOnOpen}
        initialSelectedTagId={initialSelectedTagId}
        tagType={tagType}
        icon={icon}
        newTagParentId={newTagParentId}
        newTagPursuitId={newTagPursuitId}
        tags={tags}
      />
    </EditTagsDialogContextProvider>
  )
}

export const EditTagsDialog: React.FC<EditTagsDialogProps> = ({
  open,
  setOpen,
  addTagOnInit,
  createNewTagOnOpen,
  initialSelectedTagId,
  tagType,
  icon = HiOutlineUserGroup,
  newTagParentId,
  newTagPursuitId,
}) => {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-[1000px] w-[75vw] h-[87vh] p-0 overflow-hidden"
        overlayClassName="overflow-hidden"
      >
        <EditTagsDialogRoot
          open={open}
          setOpen={setOpen}
          addTagOnInit={addTagOnInit}
          createNewTagOnOpen={createNewTagOnOpen}
          initialSelectedTagId={initialSelectedTagId}
          tagType={tagType}
          icon={icon}
          newTagParentId={newTagParentId}
          newTagPursuitId={newTagPursuitId}
        />
      </DialogContent>
    </Dialog>
  )
}
